// this is "share-collab"
import { atom } from "jotai";

import { Logger } from "@moment/logging";

import { $trackDocumentPublished } from "../../analytics";
import { $accessTokenFunc } from "../../authenication";
import { $gitStatus } from "../../git";
import { type Workspace } from "../../workspaces/workspace.types";
import {
	$setDocumentPublished,
	$setDocumentPublishedWithPassword,
	$setRemoteWorkspace,
	$updateDocumentPermissions,
} from "../sharing";
import type { PermissionUpdate } from "../sharing/permissions.types";
import { type PlannedOutcomeResult } from "./current-state.workflow.atom";
import { $remoteDocumentMetadataExists } from "./docsapi.atom";
import {
	NoDocumentIdError,
	NoWorkspaceError,
	NotAuthenticatedError,
	PlanNotAchievableError,
	RemoteRepoDoesNotExistError,
	UnknownError,
	WorkflowError,
} from "./errors";
import {
	$commitChanges,
	$createPublishRepo,
	$createRemote,
	$initializeGitRepo,
	$pushToRemote,
	$setTrackingBranch,
	$syncMainToCollab,
	$verifyRemoteExists,
	initialCommit,
} from "./mutations.atom";
import {
	$appendToPublishPlan,
	$isSyncing,
	$setPublishPlan,
	$syncAction,
	$syncActionWithLogging,
	$syncError,
	$syncState,
} from "./sync-state.atom";
import type { SyncAction, SyncSnapshot } from "./sync.types";

const logger = new Logger("publish.workflow.atom");

const setAction = (
	set: any,
	documentId: string,
	action: SyncAction | undefined,
	dryRun?: boolean
) => {
	if (dryRun && action) {
		set($appendToPublishPlan, { documentId, action });
		return;
	}
	set($syncActionWithLogging, {
		documentId,
		action,
		workflow: "publish",
	});
};

export const $executeUnpublishWorkflow = atom(
	null,
	async (
		get,
		set,
		input: {
			snap: SyncSnapshot;
		}
	) => {
		logger.debug("Starting unpublish workflow", { input });
		const { snap } = input;

		const documentId = snap.documentId;
		try {
			if (!documentId) {
				throw new NoDocumentIdError("unpublish");
			}
			set($isSyncing(documentId), true);
			if (snap.isPublished === false) {
				logger.debug("Document is already unpublished, skipping unpublish workflow", {
					documentId,
				});
				return;
			}
			const workspace = snap.publishWorkspace;
			if (!workspace) {
				throw new NoWorkspaceError("unpublish");
			}
			const { getAccessTokenSilently } = get($accessTokenFunc);
			if (!getAccessTokenSilently) {
				throw new NotAuthenticatedError("unpublish");
			}

			await set($setDocumentPublished, {
				isPublished: false,
				documentId,
				workspace,
			});
		} catch (error) {
			logger.error("Error executing unpublish workflow", {
				error,
				documentId,
			});
			if (documentId) {
				set($syncError(documentId), error as Error);
			}
		} finally {
			if (documentId) {
				set($isSyncing(documentId), false);
			}
		}
	}
);

// This publishing a document and pushes the changes.
// If remote exists, it will just push.
export const $executePublishAndPushWorkflow = atom(
	null,
	async (
		get,
		set,
		input: {
			commitMessage: string;
			snap: SyncSnapshot;
			plannedOutcome: PlannedOutcomeResult;
			pendingPermissions?: Map<string, PermissionUpdate>;
			workspace?: Workspace;
			dryRun?: boolean;
			password?: string;
		}
	) => {
		const { dryRun } = input;
		const documentId = input.snap.documentId!;
		logger.debug("Starting share sync workflow", { input, dryRun });

		// Reset plan at start if dry-run
		if (dryRun) {
			set($setPublishPlan, { documentId, plan: [] });
		} else {
			set($syncState(documentId), { isSyncing: true, workflow: "publish" });
		}

		try {
			// STEP 0: GET INITIAL STATE DATA
			const { snap, plannedOutcome, pendingPermissions } = input;
			let publishWorkspace = snap.publishWorkspace;

			//
			// VALIDATE DATA
			//
			// Validate if we can even execute
			if (!documentId) {
				throw new NoDocumentIdError("publish");
			}

			if (!plannedOutcome.canExecute) {
				logger.error("Cannot execute share sync workflow, plan not achievable", {
					planStatus: plannedOutcome.planStatus,
					plannedOutcome: plannedOutcome.plannedOutcome,
				});
				throw new PlanNotAchievableError("publish");
			}

			if (!snap.remoteRepoExists && !input.workspace) {
				logger.error(
					"Cannot execute share sync workflow, no workspace provided for remote repo creation"
				);
				throw new RemoteRepoDoesNotExistError("publish");
			}

			//
			// EXECUTE SYNC ACTIONS
			//

			// STEP 1: Local git stuff. Ensure git is initialized and commit any outstanding changes

			let sourceCommit: string | undefined;

			if (!snap.gitRepoStatus) {
				logger.debug("Initializing git repository");
				setAction(set, documentId, "initialize-git-repo", dryRun);
				await set($initializeGitRepo, { documentId, dryRun });
				setAction(set, documentId, "commit-initial-changes", dryRun);
				sourceCommit = await initialCommit(set, { documentId, dryRun });
				logger.debug("Git repository initialized");
			}

			if (!snap.remoteRepoExists) {
				logger.debug("Creating remote repository");
				setAction(set, documentId, "create-publish-repo", dryRun);
				await set($createPublishRepo, { documentId, workspace: input.workspace!, dryRun });
				if (!snap.documentRemoteSet) {
					setAction(set, documentId, "create-remote", dryRun);
					await set($createRemote, { documentId, workspace: input.workspace!, dryRun });
				}

				if (!snap.repoClean) {
					setAction(set, documentId, "commit-outstanding-changes", dryRun);
					sourceCommit = await set($commitChanges, {
						documentId,
						message: input.commitMessage,
						dryRun,
					});
				}

				if (snap.isOnCollabBranch) {
					// Sync main bookmark to collab/main's described commit
					// so they match, then push that commit as main.
					setAction(set, documentId, "sync-main-to-collab", dryRun);
					if (!dryRun) {
						sourceCommit = await set($syncMainToCollab, documentId);
					}
					setAction(set, documentId, "push-to-remote", dryRun);
					await set($pushToRemote, { documentId, sourceCommit, dryRun });
				} else {
					setAction(set, documentId, "push-to-remote", dryRun);
					await set($pushToRemote, { documentId, sourceCommit, dryRun });
				}

				setAction(set, documentId, "set-tracking-branch", dryRun);
				await set($setTrackingBranch, { documentId, dryRun });

				publishWorkspace = input.workspace!;
				logger.debug(
					"Remote repository created and commits pushed. Publish workspace set",
					{ publishWorkspace }
				);
			} else if (!snap.repoClean) {
				// STEP 2: COMMIT AND PUSH OUTSTANDING CHANGES (for existing repos)
				setAction(set, documentId, "commit-outstanding-changes", dryRun);
				sourceCommit = await set($commitChanges, {
					documentId,
					message: input.commitMessage,
					dryRun,
				});

				if (snap.isOnCollabBranch) {
					// Sync main bookmark to collab/main's described commit
					// so they match, then push that commit as main.
					setAction(set, documentId, "sync-main-to-collab", dryRun);
					if (!dryRun) {
						sourceCommit = await set($syncMainToCollab, documentId);
					}
					setAction(set, documentId, "push-to-remote", dryRun);
					await set($pushToRemote, { documentId, sourceCommit, dryRun });
				} else {
					setAction(set, documentId, "push-to-remote", dryRun);
					await set($pushToRemote, { documentId, sourceCommit, dryRun });
				}
			} else if (snap.repoAhead) {
				// STEP 2b: PUSH ALREADY-COMMITTED CHANGES (repo is clean but ahead)
				// No commit just happened — look up the parent of the WC draft
				if (!dryRun) {
					const status = await get($gitStatus(documentId));
					if (status?.isDraft) {
						const { $gitHistory } = await import("../../git");
						const history = await get($gitHistory(documentId));
						const wc = history?.commits.find((c) => c.hash === status.commit);
						sourceCommit = wc?.parents[0];
					}
				}

				if (snap.isOnCollabBranch) {
					// Sync main bookmark to collab/main's described commit
					// so they match, then push that commit as main.
					setAction(set, documentId, "sync-main-to-collab", dryRun);
					if (!dryRun) {
						sourceCommit = await set($syncMainToCollab, documentId);
					}
					setAction(set, documentId, "push-to-remote", dryRun);
					await set($pushToRemote, { documentId, sourceCommit, dryRun });
				} else {
					setAction(set, documentId, "push-to-remote", dryRun);
					await set($pushToRemote, { documentId, sourceCommit, dryRun });
				}
			}

			// STEP 3: UPDATE PERMISSIONS AND PUBLISH
			setAction(set, documentId, "verify-remote-exists", dryRun);

			await set($verifyRemoteExists, {
				documentId,
				workspace: publishWorkspace!,
				dryRun,
			});
			if (pendingPermissions && pendingPermissions.size > 0 && !dryRun) {
				await set($updateDocumentPermissions, {
					documentId,
					workspaceName: publishWorkspace!.name,
					updates: pendingPermissions,
				});
			}

			// update permissions to publish the document
			// if the planned outcome includes publishing
			if (plannedOutcome.plannedOutcome === "publish") {
				setAction(set, documentId, "publish-document", dryRun);
				if (!dryRun) {
					if (input.password) {
						await set($setDocumentPublishedWithPassword, {
							documentId,
							workspace: publishWorkspace!,
							password: input.password,
						});
					} else {
						await set($setDocumentPublished, {
							isPublished: true,
							documentId,
							workspace: publishWorkspace!,
						});
					}
				}
			}

			// CLEANUP (skip in dry-run mode)
			if (!dryRun) {
				set($setRemoteWorkspace, { documentId, workspace: publishWorkspace! });
				set($syncError(documentId), null);
				set($remoteDocumentMetadataExists(documentId)); // force refetch

				// Track publish analytics
				set($trackDocumentPublished, {
					documentId,
					workspaceSlug: publishWorkspace!.slug,
					isFirstPublish: !snap.isPublished,
				});

				logger.debug("Updating document permissions after share collab workflow");
				logger.debug("Permissions updated");
				logger.debug("Share collab workflow completed");
			}
		} catch (error) {
			// In dry-run mode, errors shouldn't set state - just log
			if (!dryRun) {
				logger.error("Error executing publish collab workflow", {
					error,
					syncAction: get($syncAction(documentId)),
					snap: input.snap,
				});
				if (!(error instanceof WorkflowError)) {
					set($syncError(documentId), new UnknownError("publish", error));
				} else {
					set($syncError(documentId), error);
				}
			}
		} finally {
			// RESET SYNC STATE (skip in dry-run mode)
			if (!dryRun) {
				set($syncState(documentId), { isSyncing: false, workflow: undefined });
				setAction(set, documentId, undefined);
				set($gitStatus(documentId));
			}
		}
	}
);
